import { fonts } from '@/theme/fonts';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Images } from '@/assets';
import OrderDetail from './order-detail/OrderDetail';
import { OrderItemsType } from '@/api/type/types';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/utils/date-time/formatDate';
import { getServiceLabel } from '@/constants/serviceTypeName';
import { SERVICE_TYPE } from '@/api/type/commonTypes';
import OrderStatusItem from './item/OrderStatusItem';
import { MultiLineText } from '@/components/text/MultiLineText';

interface columnsProps {
  key: string;
  label: string;
  width: string;
  align: string;
}

interface OrderListItemProps {
  items: OrderItemsType[];
}

const OrderListItem = ({ items }: OrderListItemProps) => {
  const [openDetailMap, setOpenDetailMap] = useState<Record<string, boolean>>(
    {}
  );
  const { i18n, t } = useTranslation();
  const columns: columnsProps[] = [
    { key: 'orderId', label: t('orderNo'), width: '11.5%', align: 'left' },
    {
      key: 'ordererClinicName',
      label: t('clinic'),
      width: '16%',
      align: 'left',
    },
    { key: 'patientName', label: t('patient'), width: '12.5%', align: 'left' },
    {
      key: 'serviceType',
      label: t('orderType'),
      width: '12%',
      align: 'left',
    },
    {
      key: 'createdAt',
      label: t('orderDate'),
      width: '12.5%',
      align: 'center',
    },
    { key: 'dueDate', label: t('dueDate'), width: '12.5%', align: 'center' },
    { key: 'status', label: t('status'), width: '12.5%', align: 'left' },
    {
      key: 'orderDetail',
      label: t('orderDetail'),
      width: '10.5%',
      align: 'center',
    },
  ];

  const onClickDetailIcon = useCallback(
    (orderNo: string) => () => {
      setOpenDetailMap((prev) => ({
        ...prev,
        [orderNo]: !prev[orderNo],
      }));
    },
    []
  );

  const renderCellValue: Record<
    (typeof columns)[number]['key'],
    (row: OrderItemsType) => React.ReactNode
  > = {
    serviceType: (row) => getServiceLabel(row.serviceType as SERVICE_TYPE),
    status: (row) => <OrderStatusItem item={row} />,
    createdAt: (row) => formatDate(row.createdAt, i18n.language),
    dueDate: (row) => formatDate(row.dueDate, i18n.language),
    orderDetail: (row) => (
      <DetailIcon
        data-selected={openDetailMap[row.orderId]}
        onClick={onClickDetailIcon(row.orderId)}
      />
    ),
    orderId: (row) => row.orderId,
    patientName: (row) => row.patientName,
    ordererClinicName: (row) => row.ordererClinicName,
  };

  return (
    <Container>
      <TableElement>
        <Thead>
          <tr>
            {columns.map((col) => (
              <Th key={col.key} $width={col.width} $align={col.align}>
                <MultiLineText translationKey={col.label} />
              </Th>
            ))}
          </tr>
        </Thead>
        <tbody>
          {items.map((row) => (
            <React.Fragment key={row.orderId}>
              <Tr>
                {columns.map((col) => {
                  const value = renderCellValue[col.key](row);

                  return (
                    <Td
                      key={col.key}
                      data-selected={openDetailMap[row.orderId]}
                    >
                      <StatusWrap
                        $align={col.align}
                        data-status={col.key === 'status'}
                      >
                        {value}
                      </StatusWrap>
                    </Td>
                  );
                })}
              </Tr>

              {openDetailMap[row.orderId] && (
                <tr key={`detail-${row.orderId}`}>
                  <DetailTd colSpan={columns.length}>
                    <OrderDetail
                      key={row.orderId}
                      orderId={row.orderId}
                      patientName={row.patientName}
                      orderStatus={row.status}
                    />
                  </DetailTd>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </TableElement>
    </Container>
  );
};

export default OrderListItem;

const Container = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const TableElement = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  box-sizing: border-box;
`;

const Thead = styled.thead`
  background-color: #769bf7;
`;

interface ThProps {
  $width: string;
  $align: string;
}

const Th = styled.th<ThProps>`
  font-family: ${fonts.family.poppins};
  font-weight: ${fonts.weight.semiBold};
  font-size: 15px;
  line-height: 120%;
  color: #fcfdff;
  height: 50px;
  padding-left: 18px;
  box-sizing: border-box;
  width: ${({ $width }) => $width};
  white-space: nowrap;
  text-align: ${({ $align }) => $align};
  &:first-child {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
  }
  &:last-child {
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
    padding-left: 0;
  }
`;

interface StatusWrapProps {
  $align: string;
}

const StatusWrap = styled.div<StatusWrapProps>`
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) => $align};
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  &[data-status='true'] {
    overflow: visible;
  }
`;

const Td = styled.td`
  padding: 12px 0 12px 18px;
  white-space: nowrap;
  border-bottom: 1px solid #769bf7;
  font-family: ${fonts.family.poppins};
  font-size: 14px;
  line-height: 120%;
  color: #000000;
  position: relative;
  overflow: visible;
  &[data-selected='true'] {
    border-bottom: none;
  }
  &:last-child {
    padding-left: 0;
  }
`;

const Tr = styled.tr`
  &:hover {
    background: rgba(118, 155, 247, 0.1);
  }
`;

const DetailTd = styled.td`
  border-bottom: 1px solid #769bf7;
`;

const DetailIcon = styled.button`
  background-image: url(${Images.DetailIcon});
  width: 48px;
  height: 38px;
  transition: all 0.2s ease;

  &[data-selected='true'] {
    background-image: url(${Images.DetailHoverIcon});
  }
`;
